import { useCallback, useEffect, useState, useMemo } from 'react';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import {
  useDataFeedWizardStore,
  useInstrumentItems,
  useOTCItems,
} from '../../../../store/dataFeedWizard/dataFeedWizardStore';
import Actions from '../../../../store/Actions';
import {
  PositionComparisonItem,
  PositionComparisonSummary,
} from '../../../../types/generatedTypes';
import { useTableSearch } from '../../../../hooks/useTableSearch';
import { UsePositionsMonitoringStepReturn, PositionWithPath } from './types';

/**
 * Hook for the Positions Monitoring step.
 * Fetches and manages position comparison data between bank and Illio.
 */
export const usePositionsMonitoringStep =
  (): UsePositionsMonitoringStepReturn => {
    const {
      sessionId,
      setCurrentStepButtonState,
      registerStepActions,
      clearStepActions,
    } = useDataFeedWizardStore();

    const instrumentMappings = useInstrumentItems();
    const otcMappings = useOTCItems();

    const [positions, setPositions] = useState<PositionComparisonItem[]>([]);
    const [summary, setSummary] = useState<PositionComparisonSummary | null>(
      null
    );
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showOnlyRelevant, setShowOnlyRelevant] = useState(false);
    const [autosizeColumns, setAutosizeColumns] = useState(false);
    const apiRef = useGridApiRef();

    const { searchTerm, handleSearch, filteredData: searchFilteredPositions } = useTableSearch(positions, { debounceMs: 200 });

    // Build set of asset names from uploaded data (instruments + OTC)
    const relevantAssetNames = useMemo(() => {
      const names = new Set<string>();
      instrumentMappings.forEach(m => {
        if (m.name) names.add(m.name.toUpperCase());
      });
      otcMappings.forEach(m => {
        if (m.name) names.add(m.name.toUpperCase());
      });
      return names;
    }, [instrumentMappings, otcMappings]);

    // Filter positions based on checkbox state - match by asset name
    const filteredPositions = useMemo(() => {
      if (!showOnlyRelevant) return searchFilteredPositions;
      return searchFilteredPositions.filter(pos => {
        const assetName = pos.asset_name?.toUpperCase();
        return assetName && relevantAssetNames.has(assetName);
      });
    }, [searchFilteredPositions, showOnlyRelevant, relevantAssetNames]);

    // Transform positions for tree data grouping by portfolio
    const groupedPositions = useMemo(() => {
      // Get unique portfolios from filtered positions
      const portfolioMap = new Map<string, { name: string; count: number }>();
      filteredPositions.forEach(pos => {
        const existing = portfolioMap.get(pos.portfolio_id);
        if (existing) {
          existing.count++;
        } else {
          portfolioMap.set(pos.portfolio_id, {
            name: pos.portfolio_name || pos.portfolio_id,
            count: 1,
          });
        }
      });

      // Create group rows and position rows with tree paths
      const result: PositionWithPath[] = [];

      portfolioMap.forEach((portfolio, portfolioId) => {
        // Add group row (portfolio header)
        result.push({
          id: `portfolio-${portfolioId}`,
          portfolio_id: portfolioId,
          portfolio_name: portfolio.name,
          asset_name: `${portfolio.name} (${portfolio.count})`,
          status: 'matched' as const,
          isGroup: true,
          path: [`portfolio-${portfolioId}`],
          // Empty values for other fields
          isin: null,
          symbol: null,
          bank_quantity: null,
          illio_quantity: null,
          quantity_delta: null,
          bank_price: null,
          illio_price: null,
          price_delta: null,
          price_delta_pct: null,
          bank_market_value: null,
          illio_market_value: null,
          value_delta: null,
          value_delta_pct: null,
          currency: '',
          custodian: '',
        });

        // Add position rows as children
        filteredPositions
          .filter(pos => pos.portfolio_id === portfolioId)
          .forEach(pos => {
            result.push({
              ...pos,
              isGroup: false,
              path: [`portfolio-${portfolioId}`, pos.id],
            });
          });
      });

      return result;
    }, [filteredPositions]);

    // Get tree data path for a row
    const getTreeDataPath = useCallback(
      (row: PositionWithPath) => row.path,
      []
    );

    // Fetch position comparison data
    const fetchPositions = useCallback(async () => {
      if (!sessionId) {
        console.warn('Missing sessionId - cannot fetch positions');
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const response = await Actions.compareWizardPositions(sessionId);

        // Sort positions: matched/alerts first, missing last
        const statusPriority: Record<string, number> = {
          matched: 1,
          quantity_alert: 2,
          price_alert: 3,
          missing_in_illio: 4,
          missing_in_bank: 5,
        };

        const sortedPositions = [...response.positions].sort((a, b) => {
          const priorityA = statusPriority[a.status] ?? 99;
          const priorityB = statusPriority[b.status] ?? 99;
          return priorityA - priorityB;
        });

        setPositions(sortedPositions);
        setSummary(response.summary);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to fetch positions';
        console.error('Failed to fetch position comparison:', err);
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    }, [sessionId]);

    // Refetch function for manual refresh
    const refetch = useCallback(async () => {
      await fetchPositions();
    }, [fetchPositions]);

    // Load data on mount
    useEffect(() => {
      fetchPositions();
    }, [fetchPositions]);

    // Update button state - this step is read-only, so button is always enabled
    useEffect(() => {
      setCurrentStepButtonState({
        disabled: false,
        loading: isLoading,
        validationMessage: undefined,
      });
    }, [isLoading, setCurrentStepButtonState]);

    // Register step actions - onNext marks session complete when user clicks "Finish"
    useEffect(() => {
      registerStepActions({
        onNext: async () => {
          if (sessionId) {
            await Actions.completeWizardSession(sessionId);
          }
        },
      });
      return () => clearStepActions();
    }, [registerStepActions, clearStepActions, sessionId]);

    // Handle autosize state changes
    useEffect(() => {
      const preservedColumns = ['__check__', '__tree_data_group__'];

      if (autosizeColumns && apiRef.current) {
        const allColumns = apiRef.current.getAllColumns();
        const visibleUserColumns = allColumns
          .filter(col => !preservedColumns.includes(col.field))
          .map(col => col.field);

        if (visibleUserColumns.length > 0) {
          apiRef.current.autosizeColumns({
            includeHeaders: true,
            includeOutliers: true,
            expand: true,
            columns: visibleUserColumns,
          });
        }
      } else if (!autosizeColumns && apiRef.current) {
        const allColumns = apiRef.current.getAllColumns();
        allColumns.forEach(col => {
          if (!preservedColumns.includes(col.field)) {
            apiRef.current?.setColumnWidth(col.field, 0);
          }
        });
      }
    }, [autosizeColumns, apiRef]);

    return {
      positions: groupedPositions,
      summary,
      isLoading,
      error,
      refetch,
      showOnlyRelevant,
      setShowOnlyRelevant,
      hasRelevantCodes: relevantAssetNames.size > 0,
      getTreeDataPath,
      autosizeColumns,
      setAutosizeColumns,
      apiRef,
      searchTerm,
      handleSearch,
    };
  };
